import { useLayoutEffect, useRef, useState } from "react"

/**
 * The width a chart is drawn at, in CSS pixels, following its container.
 *
 * Measured rather than scaled through a `viewBox`, because a scaled SVG scales
 * its text with it: an axis label set at 11px would render at whatever the
 * card's width made of it. Until the first measurement — and in a test, where
 * nothing lays anything out — it is `fallback`.
 */
export function useWidth<T extends HTMLElement>(fallback: number) {
  const ref = useRef<T>(null)
  const [width, setWidth] = useState(fallback)

  useLayoutEffect(() => {
    const element = ref.current
    if (element === null) return
    const measure = () => {
      const measured = Math.floor(element.getBoundingClientRect().width)
      if (measured > 0) setWidth(measured)
    }
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(element)
    return () => observer.disconnect()
  }, [])

  return [ref, width] as const
}
